import React from 'react';
import { Dropdown, Button } from 'antd';
import { useNavigate } from 'react-router-dom';
import { ItemType } from 'antd/lib/menu/hooks/useItems';
import { ReactComponent as ActionSvg } from '@assets/icons/action.svg';
import { ReactComponent as UpgradePlanSvg } from '@assets/icons/diamond-upgrade.svg';
import { assetGetAssetsDownloadList } from '@api/Asset';
import { useDispatch } from 'react-redux';
import { AssetItemDto } from '@api/Api';
import { restoreDeletedFolderAction } from '@redux/actions/assetsAction';
import {
  toggleApprovalConflictAssetsModal,
  toggleChangeFolderAccessModal,
  toggleCopyModal,
  toggleEntityDeletionModal,
  toggleManageFolderAccessModal,
  toggleRenameEntityModal,
  toggleShareAssetModal
} from '@redux/actions/modalAction';
import { downloadAll } from '@helpers/download';
import { useOrganization } from '@components/OrganizationBoundary';
import UpgradePlanTooltip from '@components/Tooltip/UpgradePlanTooltip';

import { ReactComponent as ApprovalUsersIconPanelSvg } from '@assets/icons/approval-users-icon-panel.svg';
import { ReactComponent as ApprovalRoutingSvg } from '@assets/icons/approval-routing.svg';
import { ReactComponent as DeleteSvg } from '@assets/icons/delete.svg';
import { ReactComponent as DownloadInvoiceSvg } from '@assets/icons/download-invoice.svg';
import { ReactComponent as EyePrivateSvg } from '@assets/icons/eye-private.svg';
import { ReactComponent as EyePublicSvg } from '@assets/icons/eye-public.svg';
import { ReactComponent as LinkSvg } from '@assets/icons/dropdown/link.svg';
import { ReactComponent as LinkPlusSvg } from '@assets/icons/dropdown/link-plus.svg';
import { ReactComponent as RenameSvg } from '@assets/icons/dropdown/rename.svg';
import { ReactComponent as CopySvg } from '@assets/icons/dropdown/copy.svg';
import { ReactComponent as DuplicateSvg } from '@assets/icons/dropdown/duplicate.svg';
import { useCopyFolderMutation } from '@hooks/queries/assets';

function FolderActions({
  node,
  canShare
}: {
  node: AssetItemDto;
  canShare?: boolean;
}) {
  const dispatch = useDispatch();
  const { currentOrganization } = useOrganization();
  const navigate = useNavigate();
  const upgradePrivateFolders =
    !currentOrganization.entity?.features.privateFolders;
  const canUseBatchApprovals =
    !!currentOrganization.entity?.features.batchApprovals;
  const awFeatureEnabled =
    !!currentOrganization.entity?.features.approvalRouting;

  const { mutateAsync: copyFolder, isPending: isCopyingFolder } =
    useCopyFolderMutation({});

  const menuItems: ItemType[] = [
    {
      key: 'download-files',
      disabled: node.folder?.assetsCount === 0,
      label: 'Download files',
      icon: <DownloadInvoiceSvg className="b-opacity-icon" />,
      onClick: async () => {
        if (!node.folder) return;
        const {
          data: { list }
        } = await assetGetAssetsDownloadList({
          campaignId: node.campaignId,
          workspaceId: node.workspaceId,
          parentId: node.id
        });
        downloadAll(
          list.map((x) => x.sourceUrl),
          node.folder.name
        );
      }
    },
    {
      key: 'new-share-link',
      label: 'New share link',
      disabled: !canShare,
      icon: <LinkPlusSvg />,
      onClick: () => {
        if (!canShare) return;
        dispatch(
          toggleShareAssetModal({
            visible: true,
            campaignId: node.campaignId,
            folders: [node.id],
            defaultName: node.folder?.name || ''
          })
        );
      }
    },
    {
      key: 'add-to-existing-link',
      label: 'Add to existing link',
      icon: <LinkSvg />
    },
    {
      type: 'divider'
    },
    ...(node?.folder?.permissions?.rename
      ? [
          {
            key: 'rename-folder',
            label: 'Rename',
            icon: <RenameSvg />,
            onClick: () => {
              dispatch(
                toggleRenameEntityModal({
                  visible: true,
                  entity: node,
                  page: 'folder'
                })
              );
            }
          }
        ]
      : []),
    {
      key: 'duplicate-folder',
      label: 'Duplicate',
      icon: <DuplicateSvg />,
      disabled: isCopyingFolder,
      onClick: () => {
        copyFolder({
          workspaceId: node.workspaceId,
          folderId: node.id,
          targetCampaignId: node.campaignId,
          copyAssets: true,
          copyComments: true,
          includeMembers: true
        });
      }
    },
    {
      key: 'copy-to',
      label: 'Copy to',
      icon: <CopySvg />,
      onClick: () => {
        dispatch(
          toggleCopyModal({
            visible: true,
            type: 'folder',
            folderId: node.id
          })
        );
      }
    },
    {
      type: 'divider'
    },
    {
      key: 'create-approval-routing',
      disabled: !canUseBatchApprovals || !awFeatureEnabled,
      icon: <ApprovalRoutingSvg className="b-opacity-icon" />,
      label: (
        <UpgradePlanTooltip
          text={
            !canUseBatchApprovals || !awFeatureEnabled
              ? ' to an Enterprise plan to route multiple media assets for approval at one time.'
              : 'Click to route all media in folder for approval'
          }
          isCanVisible={true}
          placement="right"
          isShowUpgradeText={!canUseBatchApprovals || !awFeatureEnabled}
          innerStyle={{
            maxWidth: '300px'
          }}
        >
          <div
            style={{
              display: 'flex',
              gap: '4px',
              alignItems: 'center'
            }}
          >
            Create approval routing
            {(!canUseBatchApprovals || !awFeatureEnabled) && (
              <UpgradePlanSvg className="upgrade-icon" />
            )}
          </div>
        </UpgradePlanTooltip>
      ),
      onClick: () => {
        if (!canUseBatchApprovals || !awFeatureEnabled) return;
        dispatch(
          toggleApprovalConflictAssetsModal({
            visible: true,
            title: 'Route folder for approval',
            campaignId: node.campaignId,
            workspaceId: node.workspaceId,
            parentId: node.id,
            callback: (aw) => {
              if (aw) {
                navigate(
                  `/campaigns/approvals/${aw.workspaceId}/${aw.campaignId}/${aw.id}`
                );
                return;
              }
              navigate(`/campaigns/single/${node.campaignId}?tab=approvals`);
            }
          })
        );
      }
    },
    ...(node?.folder?.permissions?.inviteContributors ||
    node?.folder?.permissions?.inviteFromList
      ? [
          {
            key: 'manage-users',
            label: 'Manage users',
            icon: <ApprovalUsersIconPanelSvg />,
            onClick: () => {
              if (!node) return;
              dispatch(
                toggleManageFolderAccessModal({
                  folder: node,
                  visible: true
                })
              );
            }
          }
        ]
      : []),
    ...(node?.folder?.permissions?.setVisibility
      ? [
          {
            key: 'change-folder-access',
            disabled: !node.isPrivate && upgradePrivateFolders,
            icon: node?.isPrivate ? (
              <EyePublicSvg className="b-opacity-icon" />
            ) : (
              <EyePrivateSvg className="b-opacity-icon" />
            ),
            label: (
              <UpgradePlanTooltip
                text=" to a Team plan to access this feature."
                isCanVisible={!node.isPrivate && upgradePrivateFolders}
                placement="right"
                innerStyle={{
                  transform: 'translate(-30px, 0)'
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    gap: '4px',
                    alignItems: 'center'
                  }}
                >
                  {node?.isPrivate ? 'Make public' : 'Make private'}
                  {!node.isPrivate && upgradePrivateFolders && (
                    <UpgradePlanSvg className="upgrade-icon" />
                  )}
                </div>
              </UpgradePlanTooltip>
            ),
            onClick: () => {
              if (!node.isPrivate && upgradePrivateFolders) return;
              if (!node) return;
              dispatch(
                toggleChangeFolderAccessModal({
                  folder: node,
                  visible: true,
                  callback: async (folder: AssetItemDto) => {
                    if (!folder.isPrivate) return;
                    dispatch(
                      toggleManageFolderAccessModal({
                        folder,
                        visible: true
                      })
                    );
                  }
                })
              );
            }
          }
        ]
      : []),
    ...(node?.folder?.permissions?.delete
      ? [
          {
            type: 'divider' as const
          },
          {
            key: 'delete-folder',
            label: 'Delete',
            icon: <DeleteSvg className="b-opacity-icon" />,
            onClick: () => {
              dispatch(
                toggleEntityDeletionModal({
                  entity: node,
                  page: 'folder',
                  permanent: false,
                  visible: true
                })
              );
            }
          }
        ]
      : [])
  ];

  const deletedItems: ItemType[] = [
    {
      key: 'delete_permanent',
      label: 'Delete forever',
      disabled: !node.folder?.permissions.delete,
      onClick: () => {
        dispatch(
          toggleEntityDeletionModal({
            entity: node,
            page: 'folder',
            permanent: true,
            visible: true
          })
        );
      }
    },
    {
      key: 'restore',
      label: 'Restore',
      disabled: !node.folder?.permissions.delete,
      onClick: () => {
        dispatch(restoreDeletedFolderAction({ folderId: node.id }));
      }
    }
  ];

  const items = node.deletedAt ? deletedItems : menuItems;
  return (
    <Dropdown
      menu={{
        items,
        onClick: (e) => {
          e.domEvent.stopPropagation();
        }
      }}
      overlayClassName="asset_links_overlay"
      placement="bottomLeft"
      trigger={['click']}
    >
      <Button type="text" size="small" style={{ padding: 0 }}>
        <ActionSvg style={{ marginRight: 0 }} />
      </Button>
    </Dropdown>
  );
}

export default FolderActions;
